import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import type { Moment } from 'moment';
import { Fetcher } from '@this/src/util';
import Notification from '@this/src/notification';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import type { AnaReservationTotalArgs } from '@this/src/domain/arrangement/ana_reservation_total';
import AnaReservationTotal, {
  convertAnaReservationTotal
} from '@this/src/domain/arrangement/ana_reservation_total';
import type { ArrangerArgs } from '@this/src/domain/arranger/arranger';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import type { ReservationType } from '../../share/reservation_type';
import { bookingTypeParamKeyOf, subTabOf } from '../../share/reservation_type';
import TotalSearchArea from './total_search_area';
import AirAutoAnaReservationTotal from './total_result_table';

interface TotalResponse {
  ana_reservations: AnaReservationTotalArgs[];
  arrangers: ArrangerArgs[];
  current_arranger: ArrangerArgs;
  total_count: number;
  total_page: number;
}

interface Props {
  reservationType: ReservationType;
}

const Total: React.FC<Props> = observer(({ reservationType }) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [anaReservationTotals, setAnaReservationTotals] = useState<AnaReservationTotal[]>([]);
  const [reservationDateFrom, setReservationDateFrom] = useState<Moment | undefined>(
    utils.getParamMoment('reservation_date_from') ?? moment().subtract(1, 'day')
  );
  const [reservationDateTo, setReservationDateTo] = useState<Moment | undefined>(
    utils.getParamMoment('reservation_date_to') ?? moment().subtract(1, 'day')
  );

  const fetchTodos = useCallback(() => {
    setIsLoading(true);
    setAnaReservationTotals([]);

    const params = {
      reservation_date_from: reservationDateFrom?.format('YYYY-MM-DD'),
      reservation_date_to: reservationDateTo?.format('YYYY-MM-DD'),
      reservation_type: reservationType
    };

    Fetcher.get<TotalResponse>('/arrangement/auto_reservation_inventory/ana_booking/total.json', params)
      .then(res => {
        setAnaReservationTotals(
          res.ana_reservations.map(args => new AnaReservationTotal(convertAnaReservationTotal(args)))
        );
      })
      .catch(e => {
        Notification.error('データの取得に失敗しました');
        utils.sendErrorObject(e);
      })
      .finally(() => setIsLoading(false));
  }, [reservationDateFrom, reservationDateTo, reservationType]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  useEffect(() => {
    const searchParams = new URLSearchParams();
    searchParams.set('tab', 'AUTO_RESERVATION_INVENTORY');
    searchParams.set('sub_tab', subTabOf(reservationType));
    searchParams.set(bookingTypeParamKeyOf(reservationType), 'TOTAL');
    if (reservationDateFrom) {
      searchParams.set('reservation_date_from', reservationDateFrom?.format('YYYY-MM-DD') ?? '');
    }
    if (reservationDateTo) {
      searchParams.set('reservation_date_to', reservationDateTo?.format('YYYY-MM-DD') ?? '');
    }
    history.replace({ search: searchParams.toString() });
  }, [history, reservationDateFrom, reservationDateTo, reservationType]);

  return (
    <>
      <TotalSearchArea
        reservationDateFrom={reservationDateFrom}
        reservationDateTo={reservationDateTo}
        setReservationDateFrom={setReservationDateFrom}
        setReservationDateTo={setReservationDateTo}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <AirAutoAnaReservationTotal
          anaReservationTotals={anaReservationTotals}
          fetchTodos={fetchTodos}
          reservationType={reservationType}
        />
      )}
    </>
  );
});

export default Total;
